/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { inject, Injectable } from '@angular/core';
import { PaginatedHttpClient } from '../paginated-http-client';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BASE_URL } from '../injection-tokens';
import { Observable } from 'rxjs';
import { ElectronicArchivingSystem } from '../models/collect/project';
import { IngestContractLight } from '../models/ingest-contract/ingest-contract';
import { ProfileLight } from '../models/profile/profile.interface';
import { AgencyLight } from '../models/agency-light/agency-light.interface';
import { map, shareReplay } from 'rxjs/operators';
import { Option } from '../components/autocomplete/utils/option.interface';

export const TENANT_SEPARATOR = ' - Tenant ';

@Injectable({
  providedIn: 'root',
})
export class ExternalReferentialService extends PaginatedHttpClient<any> {
  #electronicArchivingSystemList$ = this.http.get<ElectronicArchivingSystem[]>(`${this.apiUrl}/config`).pipe(shareReplay(1));

  constructor() {
    const http = inject(HttpClient);
    const baseUrl = inject(BASE_URL);

    super(http, baseUrl + '/external-referential');
  }

  getAgencies(archivingSystemId: string, tenantIdentifier: number): Observable<AgencyLight[]> {
    const params = new HttpParams().set('archivingSystemId', archivingSystemId).set('tenantIdentifier', tenantIdentifier);
    return this.http.get<AgencyLight[]>(`${this.apiUrl}/agencies`, { params });
  }

  archivalIngestContracts(archivingSystemId: string, tenantIdentifier: number): Observable<IngestContractLight[]> {
    const params = new HttpParams().set('archivingSystemId', archivingSystemId).set('tenantIdentifier', tenantIdentifier);
    return this.http.get<IngestContractLight[]>(`${this.apiUrl}/ingestcontracts`, { params });
  }

  archiveProfiles(archivingSystemId: string, tenantIdentifier: number): Observable<ProfileLight[]> {
    const params = new HttpParams().set('archivingSystemId', archivingSystemId).set('tenantIdentifier', tenantIdentifier);
    return this.http.get<ProfileLight[]>(`${this.apiUrl}/profiles`, { params });
  }

  getElectronicArchivingSystemList$(): Observable<ElectronicArchivingSystem[]> {
    return this.#electronicArchivingSystemList$;
  }

  getElectronicArchivingSystemOptions$(): Observable<Option[]> {
    return this.#electronicArchivingSystemList$.pipe(
      map((list) => (Array.isArray(list) ? list : [])),
      map((list) =>
        list.flatMap((system) =>
          system.tenantIds.map((tenantId: number) => ({
            key: `${system.archivingSystemId}${TENANT_SEPARATOR}${tenantId}`,
            label: `${system.name}${TENANT_SEPARATOR}${tenantId}`,
          })),
        ),
      ),
    );
  }
}
